import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'

import { ConvertOrderDialog } from '../components/ConvertOrderDialog'
import { useGetNetworksQuery, useConvertMeetingLinkToConsultationMutation, useConvertMeetingLinkToExpertiseMutation } from '@/core/services/network'
import { AvailabilityMeetingPlace } from '@/core/models/meeting-link.model'
import { getApiErrorMessage } from '@/utils/errors'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

interface ConvertOrderDialogContainerProps {
  open: boolean
  onClose: () => void
  orderId: string
  meetingPlace: AvailabilityMeetingPlace
  onSuccess?: () => void
}

export const ConvertOrderDialogContainer = ({ open, onClose, orderId, meetingPlace, onSuccess }: ConvertOrderDialogContainerProps) => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const [isConverting, setIsConverting] = useState(false)
  const authUser = useAppSelector(selectAuthUser)

  const { data: rawNetworks = [] } = useGetNetworksQuery({}, {
    refetchOnMountOrArgChange: true
  })

  // Filter networks to only show joined ones
  const networks = rawNetworks.filter(network =>
    network.members.find(member => member === authUser?._id)
  )

  const [convertToConsultation] = useConvertMeetingLinkToConsultationMutation()
  const [convertToExpertise] = useConvertMeetingLinkToExpertiseMutation()

  const handleConvert = async (networkId: string) => {
    try {
      setIsConverting(true)
      if (meetingPlace === AvailabilityMeetingPlace.SECOND_OPINION) {
        await convertToExpertise({ networkId, orderId }).unwrap()
      } else {
        await convertToConsultation({ networkId, orderId }).unwrap()
      }

      enqueueSnackbar(t('notification.order.convert.success'), { variant: 'success' })
      onClose()
      onSuccess?.()
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.order.convert.error')), { variant: 'error' })
    } finally {
      setIsConverting(false)
    }
  }

  return (
    <ConvertOrderDialog
      open={open}
      onClose={onClose}
      networks={networks}
      onConvert={handleConvert}
      meetingPlace={meetingPlace}
      isConverting={isConverting}
    />
  )
} 